import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  CircleQuestionMarkIcon,
  CircleUserIcon,
  SettingsIcon,
  MailIcon,
  MenuIcon,
  PowerIcon,
  RocketIcon,
  SearchIcon,
  SendIcon,
  TrophyIcon,
  UserIcon,
} from "lucide-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { parseDate } from "simplydo/core/dates";
// @ts-ignore
import { languages, setInterpolationDictionary } from "simplydo/core/i18n";
import { DIMENSIONS, getImageUrl } from "simplydo/core/images";
import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";

import actions from "actions";
import api, { websocketApi } from "api";
import { useAppDispatch, useAppSelector } from "store";
import { getStoredGroupVisits } from "utils/groupVisits";
import util from "utils/utils";

import ManageLinksModal from "components/admin/Links/ManageLinksModal";
import { useOrgNavBar } from "components/admin/Settings/Branding";
import MessageTray from "components/lib/Messaging/MessageTray";
import NotificationTray from "components/lib/Notifications";
import OmniBar from "components/lib/OmniBar";

import OrgSwitcherTray from "../OrgSwitcherTray";
import type { NavBarProfileUpdate } from "./types";

const openChatwoot = () => {
  if (!window.$chatwoot) {
    util
      .confirm(
        "Unable to open chat support",
        "Something went wrong while trying to open our chat support. You may have content blocking installed which interferes with our chat widget. Please disable content blocking and try again.",
      )
      .then(() => {})
      .catch(() => {});
    return;
  }
  window.$chatwoot.toggle();
};

const StyledNavBar = styled.header`
  background: rgb(255, 255, 255);
  flex: 0 0 50px;
  height: 50px;
  gap: ${({ theme }) => (theme.sizes.isMobile ? 10 : 20)}px;
  position: sticky;
  width: 100%;
  top: 0;
  box-shadow:
    0px 6px 6px -5px rgba(0, 0, 0, 0.08),
    0px 1px 3px -2px rgba(0, 0, 0, 0.1) !important;
  display: flex;
  align-content: stretch;
  align-items: stretch;
  justify-content: space-between;
  z-index: 1000;
`;

const StyledNavBarSegment = styled.div<{ $alignRight?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $alignRight }) =>
    $alignRight ? "flex-end" : "flex-start"};
  &:first-of-type {
    margin-left: 10px;
  }
  &:last-of-type {
    margin-right: 10px;
  }
`;

const StyledLogo = styled(Link)`
  margin-right: 10px;
  img {
    vertical-align: middle;
    max-height: 40px;
    max-width: ${({ theme }) => (!theme.sizes.isComputer ? 100 : 150)}px;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  flex: 1;
  flex-grow: 1;
  align-items: center;
  text-transform: capitalize;
`;

const StyledNavIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: 20px;
  height: 30px;
  width: 30px;
  border-radius: 50%;
  background-color: rgb(240, 240, 240);
  transition: background-color 0.25s;
  cursor: pointer;
  margin-right: ${({ theme }) => (theme.sizes.isMobile ? "10px" : "20px")};
  position: relative;

  a,
  ${Icon} {
    transition: color 0.25s;
    color: rgb(120, 136, 150);
  }
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.solid};
    a,
    ${Icon} {
      color: ${({ theme }) => theme.colors.primary.onSolid};
    }
  }
`;

const StyledNavAvatar = styled.div<{ $user?: OpenAPI.Schemas["User"] }>`
  width: 30px;
  height: 30px;
  cursor: pointer;
  border-radius: 50%;
  overflow: hidden;
  background-color: white;
  background-size: cover;
  background-position: center center;
  margin-right: 0px;
  background-image: ${({ $user }) =>
    `url(${util.mixinCssUrlFallback(util.avatarUrl($user), util.avatarUrl())})`};
  outline: 1px solid #e2e2e2;
  transition: box-shadow 0.25s;
  &:hover {
    box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.1);
  }
`;

const StyledMobileSearchBar = styled.div`
  padding-top: 0px;
  padding-bottom: 0px;
  background-color: #fff;
  position: fixed;
  width: 100%;
  z-index: 100;
  top: 50px;
`;

const asTimestamp = (value?: string | null) => {
  return parseDate(value)?.getTime() || 0;
};

const getRecentGroups = (user: UserMe | null) => {
  if (!user?.groups?.length) return [];

  const groups = user.groups;
  const groupVisits = getStoredGroupVisits(
    user._id,
    user.ownerOrganisation?._id,
  );

  return [...groups]
    .sort((a, b) => {
      const visitDiff =
        asTimestamp(groupVisits[b._id ?? ""]) -
        asTimestamp(groupVisits[a._id ?? ""]);
      if (visitDiff) return visitDiff;

      const aFallback =
        asTimestamp(a.activityInfo?.lastActivityAt) ||
        asTimestamp(a.updatedAt) ||
        asTimestamp(a.createdAt);
      const bFallback =
        asTimestamp(b.activityInfo?.lastActivityAt) ||
        asTimestamp(b.updatedAt) ||
        asTimestamp(b.createdAt);
      if (bFallback !== aFallback) return bFallback - aFallback;

      return (a.name || "").localeCompare(b.name || "");
    })
    .filter((group): group is typeof group & { _id: string } =>
      Boolean(group._id),
    )
    .slice(0, 6);
};

const navItemTitle = (
  item: { type?: string; title?: string; translate?: string },
  t: (key: string, options?: { key?: string }) => string,
) => {
  if (item.type === "link") return item.title ?? "";
  return (
    t("common:capitalise", { key: item.translate ?? "" }) || item.title || ""
  );
};

const isNavItemSelected = (
  item: { id?: string; type?: string; location?: string },
  location: ReturnType<typeof useLocation>,
) => {
  if (item.type === "link") {
    const itemLocationPath = new URL(
      item.location ?? "",
      window.location.origin,
    ).pathname;
    return itemLocationPath !== "/" && location.pathname === itemLocationPath;
  }
  if (item.id === "home") return location.pathname === "/";
  if (item.id === "groups") {
    return util.activePath(/^\/groups([A-z0-9/])*\w*/g, location);
  }
  if (item.id === "admin") {
    return util.activePath(/^\/admin([A-z0-9/])*\w*/g, location);
  }
  return location.pathname.startsWith(`/${item.location}`);
};

export const SimpleNavBar = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const theme = useTheme();
  const navigate = useNavigate();

  const urlOrganisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );

  const organisation = user?.ownerOrganisation || urlOrganisation;

  const userId = user?._id;
  const switchOrganisation = useCallback(
    (orgId: string) => {
      if (!userId) return;

      api.users.switchOrganisation(
        userId,
        orgId,
        (data) => {
          dispatch(actions.user.switchOrganisation(data.organisation));
        },
        () => {},
      );
    },
    [userId, dispatch],
  );

  const unGhost = () => {
    api.users.unGhost(
      () => {
        api.queryClient.invalidateQueries();
        dispatch(actions.user.unGhost());
      },
      () => {},
    );
  };

  const logout = () => {
    api.auth.logout(
      () => {
        navigate("/");
      },
      () => {},
    );
  };

  return (
    <StyledNavBar>
      <StyledNavBarSegment style={{ flex: 1, flexGrow: 1 }}>
        <StyledLogo to="/">
          <img
            alt=""
            src={getImageUrl(
              organisation,
              "darkLogo",
              [
                [DIMENSIONS.SCALE, 200],
                [DIMENSIONS.SCALE, 500],
              ],
              "images/defaults/org-logo.png",
            )}
          />
        </StyledLogo>
      </StyledNavBarSegment>

      <StyledNavBarSegment $alignRight style={{ flex: 1, flexGrow: 1 }}>
        {user && theme.sizes.isComputer && (
          <StyledNavIcon
            role="button"
            tabIndex={0}
            onClick={openChatwoot}
            onKeyDown={handleClickForKeyboard(openChatwoot)}
          >
            <Icon icon={CircleQuestionMarkIcon} />
          </StyledNavIcon>
        )}
        {user ? (
          <Dropdown
            trigger={<StyledNavAvatar $user={user} />}
            direction="left"
            upward
          >
            <DropdownMenu style={{ margin: "5p 0 0 0" }}>
              <DropdownHeader>
                <Icon icon={CircleUserIcon} /> {user?.profile?.fullName}
              </DropdownHeader>
              {user?.ownerOrganisations?.length > 1 && (
                <>
                  <DropdownDivider />
                  <DropdownHeader>Switch account</DropdownHeader>
                  {user.ownerOrganisations.map((o) => (
                    <DropdownItem
                      key={o._id}
                      onClick={() => switchOrganisation(o._id)}
                    >
                      <span style={{ width: 50, marginRight: 5 }}>
                        <img
                          alt={`${o.name} logo`}
                          src={getImageUrl(
                            o,
                            "darkLogo",
                            [
                              [DIMENSIONS.SCALE, 100],
                              [DIMENSIONS.SCALE, 200],
                              [DIMENSIONS.SCALE, 500],
                            ],
                            "images/defaults/org-logo.png",
                          )}
                          style={{
                            maxWidth: 50,
                            maxHeight: 35,
                            verticalAlign: "middle",
                          }}
                        />
                      </span>
                      {o.name}
                    </DropdownItem>
                  ))}
                </>
              )}
              <DropdownDivider />
              {user?.ghostingAs ? (
                <DropdownItem onClick={unGhost}>
                  <Icon icon={PowerIcon} /> Unghost user
                </DropdownItem>
              ) : (
                <DropdownItem onClick={logout}>
                  <Icon icon={PowerIcon} /> {t("generic.logout")}
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        ) : null}

        {user ? <OrgSwitcherTray /> : null}
      </StyledNavBarSegment>
    </StyledNavBar>
  );
};

const NavBar = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const userId = user?._id;

  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [omniSearchOpen, setOmniSearchOpen] = useState(false);
  const [manageLinksModalOpen, setManageLinksModalOpen] = useState(false);
  const profileUpdateRef = useRef<ReturnType<
    typeof websocketApi.subscribe
  > | null>(null);
  const selectedLanguage = languages.filter(
    (l) => l.key === (i18n.language || "en"),
  )[0];

  const urlOrganisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const newMessageCount = useAppSelector(
    (state) => state.messages.newMessageCount,
  );

  const organisation = user?.ownerOrganisation || urlOrganisation;
  const orgId = organisation?._id;

  const orgNavBar = useOrgNavBar();

  const visibleNavItems = orgNavBar.entries.slice(0, 5);
  const moreNavItems = orgNavBar.entries.slice(5);
  const recentGroups = useMemo(() => getRecentGroups(user), [user]);

  const [pinnedPages, setPinnedPages] = useState<
    OpenAPI.GET<"/pages/pinned/{for_type}/{for_id}">["response"]["pages"]
  >([]);

  const updateProfile = useCallback(
    (profile: NavBarProfileUpdate) => {
      dispatch(actions.user.updateProfile(profile));
    },
    [dispatch],
  );

  const getPinnedPages = useCallback(() => {
    if (!orgId) return;

    api.pages.getPinned(
      "organisation",
      orgId,
      ({ pages: newPages }) => {
        setPinnedPages(newPages);
      },
      () => {},
    );
  }, [orgId]);

  useEffect(() => {
    getPinnedPages();
  }, [getPinnedPages]);

  const unGhost = () => {
    api.users.unGhost(
      () => {
        api.queryClient.invalidateQueries();
        dispatch(actions.user.unGhost());
      },
      () => {},
    );
  };

  const logout = () => {
    api.auth.logout(
      () => {
        navigate("/");
      },
      () => {},
    );
  };

  const switchOrganisation = useCallback(
    (orgId: string) => {
      if (!userId) return;

      api.users.switchOrganisation(
        userId,
        orgId,
        (data) => {
          dispatch(actions.user.switchOrganisation(data.organisation));
        },
        () => {},
      );
    },
    [userId, dispatch],
  );

  // Subscribe to websocket updates to check if Microsoft SSO has applied a profile pic automatically
  // It's highly possible the websocket subscription "misses" a Microsoft profile picture update; Simply because the event that sets the profile picture could conceivably finish in the nether zone between the point where /users/me() has been returned to the client, and before we are subscribed to websockets.
  // There's nothing we can do about this - Therefore, to cover this gap, if we know the user has logged in with SSO, we make a single request to get their profile and load in the profile image if it's not already there.

  const isSsoUser = !!user?.ssoUser;
  const userHasAvatarImage = !!user?.profile?.avatar;
  useEffect(() => {
    if (userId && isSsoUser && !userHasAvatarImage) {
      profileUpdateRef.current = websocketApi.subscribe(
        `userProfileUpdated-${userId}`,
        (data) => {
          updateProfile(data.profile);
        },
        { receiveErrorUpdates: false, receiveUserUpdates: false },
      );
      api.users.get(
        userId,
        (data) => {
          if (data?.profile?.avatar && data?.profile?.avatarUrl) {
            updateProfile({
              avatar: data.profile.avatar,
              avatarUrl: util.avatarUrl(data) as string,
              avatarWasSetByExternalSource:
                data.profile.avatarWasSetByExternalSource ?? undefined,
            });
          }
        },
        () => {},
      );
    }
    return () => {
      if (profileUpdateRef.current) {
        profileUpdateRef.current.unsubscribe();
      }
    };
  }, [isSsoUser, updateProfile, userId, userHasAvatarImage]);

  const shouldShowMoreMenu = useMemo(
    () => moreNavItems.length > 0 || !!pinnedPages.length,
    [moreNavItems.length, pinnedPages.length],
  );

  const entryUrls = [
    "/login",
    "/login/organisation",
    "/enrol",
    "/agree-terms",
    "/password/forgotten",
    "/password/reset",
  ];
  const hideLoginButton =
    entryUrls.some((url) => location.pathname.includes(url)) ||
    location.pathname === "/";
  return (
    <>
      <ManageLinksModal
        isOpen={manageLinksModalOpen}
        onClose={() => setManageLinksModalOpen(false)}
      />

      <StyledNavBar>
        <StyledNavBarSegment style={{ flex: 1, flexGrow: 1 }}>
          <StyledLogo to="/">
            <img
              alt=""
              src={getImageUrl(
                organisation,
                "darkLogo",
                [
                  [DIMENSIONS.SCALE, 200],
                  [DIMENSIONS.SCALE, 500],
                ],
                "images/defaults/org-logo.png",
              )}
            />
          </StyledLogo>
          <ActionContainer>
            {!theme.sizes.isComputer ? (
              user && (
                <Dropdown
                  trigger={
                    <StyledNavIcon>
                      <Icon icon={MenuIcon} />
                    </StyledNavIcon>
                  }
                  style={{ zIndex: 999 }}
                >
                  <DropdownMenu style={{ margin: "5px 0 0 0" }}>
                    {user &&
                      orgNavBar.entries.map((item) => {
                        if (
                          item.id === "groups" &&
                          !user.groupsAvailable &&
                          !util.hasPermission(
                            user,
                            "org.createGroups",
                            user.ownerOrganisation?._id ?? "",
                          )
                        ) {
                          return null;
                        }

                        if (item.type === "link") {
                          return (
                            <DropdownItemLink
                              key={item.id}
                              to={item.location ?? ""}
                              target={item.external ? "_blank" : undefined}
                            >
                              {item.title ?? ""}
                            </DropdownItemLink>
                          );
                        }
                        return (
                          <DropdownItemLink
                            key={item.id}
                            to={item.location ?? ""}
                          >
                            <span style={{ textTransform: "capitalize" }}>
                              {item.title || t(item.translate ?? "")}
                            </span>
                          </DropdownItemLink>
                        );
                      })}
                  </DropdownMenu>
                </Dropdown>
              )
            ) : (
              <React.Fragment>
                {user &&
                  visibleNavItems.map((item) => {
                    const canAccessGroups =
                      item.id === "groups" &&
                      (user.groupsAvailable ||
                        util.hasPermission(
                          user,
                          "org.createGroups",
                          user.ownerOrganisation?._id ?? "",
                        ));
                    const canAccessAdmin =
                      item.id === "admin" &&
                      util.hasPermission(
                        user,
                        "org.viewDashboard",
                        user.ownerOrganisation?._id ?? "",
                      );

                    if (item.id === "admin" && !canAccessAdmin) {
                      return null;
                    }

                    const trigger = (
                      <ButtonLink
                        key={item.id}
                        to={
                          item.id === "groups" || item.id === "admin"
                            ? `/${item.location}`
                            : (item.location ?? "")
                        }
                        target={item.external ? "_blank" : undefined}
                        color="primary"
                        size="small"
                        variant={
                          isNavItemSelected(item, location)
                            ? "regular"
                            : "ghost"
                        }
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {navItemTitle(item, t)}
                      </ButtonLink>
                    );

                    if (canAccessGroups && recentGroups.length > 0) {
                      return (
                        <Dropdown key="groups" on="hover" trigger={trigger}>
                          <DropdownMenu style={{ margin: "5px 0 0 0" }}>
                            <DropdownHeader>
                              {t("navbar.yourGroups")}
                            </DropdownHeader>
                            {recentGroups.map((group) => (
                              <DropdownItemLink
                                key={group._id}
                                to={`/groups/${group._id}`}
                              >
                                {group.name}
                              </DropdownItemLink>
                            ))}
                          </DropdownMenu>
                        </Dropdown>
                      );
                    }

                    if (canAccessAdmin) {
                      return (
                        <Dropdown key="admin" on="hover" trigger={trigger}>
                          <DropdownMenu style={{ margin: "5px 0 0 0" }}>
                            <DropdownHeader>
                              {t("navbar.quickAccess")}
                            </DropdownHeader>
                            <DropdownItem
                              onClick={() => setManageLinksModalOpen(true)}
                            >
                              <Icon icon={SettingsIcon} />{" "}
                              {t("navbar.manageShortLinks")}
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      );
                    }

                    return trigger;
                  })}
                {user && shouldShowMoreMenu ? (
                  <Dropdown
                    on="hover"
                    trigger={
                      <Button color="primary" size="small" variant="ghost">
                        <Icon name="dropdown" />
                        {t("generic.more")}
                      </Button>
                    }
                  >
                    <DropdownMenu style={{ margin: "5px 0 0 0" }}>
                      {moreNavItems.length > 0 && (
                        <DropdownHeader>
                          {t("navbar.moreLocations")}
                        </DropdownHeader>
                      )}
                      {moreNavItems.map((item) => {
                        if (item.type === "link") {
                          return (
                            <DropdownItemLink
                              key={item.id}
                              to={item.location ?? ""}
                              target={item.external ? "_blank" : undefined}
                            >
                              {item.title}
                            </DropdownItemLink>
                          );
                        }

                        return (
                          <DropdownItemLink
                            key={item.id}
                            to={item.location ?? ""}
                          >
                            <span style={{ textTransform: "capitalize" }}>
                              {item.title || t(item.translate ?? "")}
                            </span>
                          </DropdownItemLink>
                        );
                      })}
                      {pinnedPages && pinnedPages.length > 0 ? (
                        <>
                          <DropdownDivider />
                          <DropdownHeader>
                            {t("navbar.pinnedPages")}
                          </DropdownHeader>
                          {pinnedPages.map((page) => (
                            <DropdownItemLink key={page._id} to={page.link}>
                              {page.title}
                            </DropdownItemLink>
                          ))}
                        </>
                      ) : null}
                    </DropdownMenu>
                  </Dropdown>
                ) : null}
              </React.Fragment>
            )}
          </ActionContainer>
        </StyledNavBarSegment>

        {user && theme.sizes.isComputer && (
          <StyledNavBarSegment style={{ flex: 1, flexGrow: 2, maxWidth: 600 }}>
            <OmniBar />
          </StyledNavBarSegment>
        )}

        <StyledNavBarSegment $alignRight style={{ flex: 1, flexGrow: 1 }}>
          {user && !theme.sizes.isComputer && (
            <StyledNavIcon
              onClick={() => setOmniSearchOpen((prevOpen) => !prevOpen)}
            >
              <Icon icon={SearchIcon} />
            </StyledNavIcon>
          )}
          {user ? (
            <NotificationTray
              style={{ marginRight: theme.sizes.isMobile ? 10 : 20 }}
            />
          ) : null}
          {user &&
            !user.ghostingAs &&
            user?.ownerOrganisation?.enabledFeatures?.includes(
              "directMessaging",
            ) &&
            (theme.sizes.isMobile ? (
              <StyledNavIcon>
                <Link to={"/messagetray"}>
                  <Icon icon={MailIcon} />
                  {newMessageCount > 0 && (
                    <Label
                      circular
                      size="small"
                      color="red"
                      style={{ top: "0.5em", left: "auto" }}
                    >
                      {newMessageCount}
                    </Label>
                  )}
                </Link>
              </StyledNavIcon>
            ) : (
              <MessageTray style={{ marginRight: 20 }} />
            ))}
          {/* @ts-ignore */}
          {user && theme.sizes.isComputer && (
            <StyledNavIcon
              role="button"
              tabIndex={0}
              onClick={openChatwoot}
              onKeyDown={handleClickForKeyboard(openChatwoot)}
            >
              <Icon icon={CircleQuestionMarkIcon} />
            </StyledNavIcon>
          )}
          {user ? (
            <Dropdown
              tabIndex={0}
              trigger={<StyledNavAvatar role="button" $user={user} />}
              direction="left"
              upward
            >
              <DropdownMenu style={{ margin: "5px 0 0 0" }}>
                <DropdownHeader>
                  <Icon icon={CircleUserIcon} /> {user?.profile?.fullName}
                </DropdownHeader>
                <DropdownItemLink to={`/users/${user._id}`}>
                  <Icon icon={UserIcon} /> {t("generic.profile")}
                </DropdownItemLink>
                <DropdownItemLink to="/invitations">
                  <Icon icon={SendIcon} /> {t("generic.invitations")}
                </DropdownItemLink>
                <DropdownItemLink to="/preferences">
                  <Icon icon={SettingsIcon} /> {t("generic.account")}
                </DropdownItemLink>
                <DropdownItemLink to="/achievements">
                  <Icon icon={TrophyIcon} /> {t("generic.achievements")}
                </DropdownItemLink>
                {user?.ownerOrganisations?.length > 1 && (
                  <>
                    <DropdownDivider />
                    <DropdownHeader>Switch account</DropdownHeader>
                    {user.ownerOrganisations.map((o) => (
                      <DropdownItem
                        key={o._id}
                        onClick={() => switchOrganisation(o._id)}
                      >
                        <span style={{ width: 50, marginRight: 5 }}>
                          <img
                            alt={`${o.name} logo`}
                            src={o.darkLogoUrl}
                            style={{
                              maxWidth: 50,
                              maxHeight: 35,
                              verticalAlign: "middle",
                            }}
                          />
                        </span>
                        {o.name}
                      </DropdownItem>
                    ))}
                  </>
                )}
                {(util.hasPermission(user, "super.viewDashboard") ||
                  util.hasPermission(
                    user,
                    "org.managePermissions",
                    user.ownerOrganisation?._id,
                  )) && <DropdownDivider />}
                {util.hasPermission(user, "super.viewDashboard") && (
                  <DropdownItemLink to="/superadmin">
                    <Icon icon={RocketIcon} /> Superadmin
                  </DropdownItemLink>
                )}
                <DropdownDivider />
                {user?.ghostingAs ? (
                  <DropdownItem onClick={unGhost}>
                    <Icon icon={PowerIcon} /> Unghost user
                  </DropdownItem>
                ) : (
                  <DropdownItem onClick={logout}>
                    <Icon icon={PowerIcon} /> {t("generic.logout")}
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          ) : null}

          {user ? <OrgSwitcherTray /> : null}
          {!user && isAuthenticated && (
            <Button color="teal" onClick={() => api?.options?.onLogout?.()}>
              Logout
            </Button>
          )}
          {!user &&
          !isAuthenticated &&
          !location.pathname.startsWith("/challenges/") ? ( // Don't show language chooser on unauthenticated challenge page
            <Dropdown
              direction="left"
              upward
              style={{ marginRight: 15 }}
              trigger={<span>{selectedLanguage?.name ?? "Language"}</span>}
            >
              <DropdownMenu>
                {languages.map((l) => (
                  <DropdownItem
                    key={l.key}
                    onClick={() => {
                      i18n.changeLanguage(l.key);
                      setInterpolationDictionary(organisation?.terminology);
                    }}
                  >
                    {l.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          ) : null}

          {!user && !isAuthenticated && !hideLoginButton ? (
            <Button
              size={theme.sizes.isMobile ? "small" : "medium"}
              onClick={() => {
                navigate(`/login?then=${location.pathname}`);
              }}
              color="primary"
            >
              {t("entry.login.loginOrSignUp")}
            </Button>
          ) : null}
        </StyledNavBarSegment>
      </StyledNavBar>

      {!theme.sizes.isComputer && omniSearchOpen && (
        <StyledMobileSearchBar>
          <OmniBar onClose={() => setOmniSearchOpen(false)} />
        </StyledMobileSearchBar>
      )}
    </>
  );
};

export default NavBar;
